import { useCallback, useEffect, useState } from "react";
import {
  ACCESS_ALARM_VOLUME_WINDOW_MINUTES,
  ACCESS_REVIEW_DEFAULT_DAYS,
  standsOut,
  type AccessAlarm,
  type AccessReviewRow,
  type AccessReviewWindow,
  type SupportAccessReviewRow,
} from "@care-by-ella/contracts";
import {
  acknowledgeAccessAlarm,
  fetchAccessAlarms,
  fetchAccessEntries,
  fetchAccessReview,
} from "@/lib/access-review-api";
import { fetchSupportAccessGrants } from "@/lib/support-access-api";

// TES-700 — the access log, made readable.
//
// The log has been written on every patient read since TES-296 and never read.
// An access log that nobody reviews meets the letter of the law and deters
// nobody, and it is the deterrence that protects the patient from lookups made
// out of curiosity.
//
// The page names the STAFF, not the patient. The reviewer needs to know who
// looked; knowing who was looked at needs no identity, only the ability to see
// that it was the same person several times. The patient therefore appears as
// an id — which is also what spares a reviewer from needing a care relationship
// with the people they review.

const WINDOWS = [7, 30, 90] as const;

function fmtDate(iso: string) {
  return new Date(iso).toLocaleString("sv-SE", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

function Entries({
  employeeId,
  days,
  onlyWithoutRelationship,
}: {
  employeeId: string;
  days: number;
  onlyWithoutRelationship?: boolean;
}) {
  const [rows, setRows] = useState<AccessReviewRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    fetchAccessEntries({ employeeId, days, onlyWithoutRelationship })
      .then((r) => live && setRows(r))
      .catch((e) => live && setError(String(e)));
    return () => {
      live = false;
    };
  }, [employeeId, days, onlyWithoutRelationship]);

  if (error) return <p className="p-sm text-sm text-danger">{error}</p>;
  if (!rows) return <p className="p-sm text-sm text-textSecondary">Hämtar…</p>;
  if (rows.length === 0)
    return (
      <p className="p-sm text-sm text-textSecondary">
        Inga åtkomster i fönstret.
      </p>
    );

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-textSecondary">
            <th className="py-xs pr-md">Tidpunkt</th>
            <th className="py-xs pr-md">Patient</th>
            <th className="py-xs pr-md">Vårdrelation</th>
            <th className="py-xs pr-md">Grund</th>
            <th className="py-xs pr-md">Ändamål</th>
            <th className="py-xs">Vy</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-surfaceMuted">
              <td className="py-xs pr-md tabular-nums">
                {fmtDate(r.occurredAt)}
              </td>
              {/* Opaque on purpose — the pattern, not the person. */}
              <td className="py-xs pr-md font-mono text-xs">
                {r.patientId.slice(0, 8)}
              </td>
              <td className="py-xs pr-md">
                {r.hadActiveRelationship ? (
                  <span className="text-textSecondary">ja</span>
                ) : (
                  <span className="font-semibold text-danger">nej</span>
                )}
              </td>
              <td className="py-xs pr-md text-textSecondary">
                {r.relationshipBasis}
              </td>
              <td className="py-xs pr-md text-textSecondary">{r.purpose}</td>
              <td className="py-xs font-mono text-xs text-textSecondary">
                {r.route}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// TES-700 — the alarms. What the sweep found in the log, waiting for someone
// to look. "Markera som granskad" records that the review happened; it is not
// a verdict on the staff member.
function alarmText(a: AccessAlarm): string {
  if (a.kind === "no_relationship") {
    return a.patientCount === 1
      ? "Öppnade 1 patient utan aktiv vårdrelation"
      : `Öppnade ${a.patientCount} patienter utan aktiv vårdrelation`;
  }
  return `Öppnade ${a.patientCount} olika patienter inom ${ACCESS_ALARM_VOLUME_WINDOW_MINUTES} minuter`;
}

function Alarms({ days }: { days: number }) {
  const [alarms, setAlarms] = useState<AccessAlarm[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setError(null);
    fetchAccessAlarms(days)
      .then((a) => live && setAlarms(a))
      .catch((e) => live && setError(String(e)));
    return () => {
      live = false;
    };
  }, [days]);

  async function acknowledge(id: string) {
    setBusy(id);
    try {
      const updated = await acknowledgeAccessAlarm(id);
      setAlarms((prev) =>
        prev ? prev.map((a) => (a.id === id ? updated : a)) : prev,
      );
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(null);
    }
  }

  const openCount = alarms?.filter((a) => !a.acknowledgedAt).length ?? 0;

  return (
    <section
      aria-labelledby="access-alarms-heading"
      className="flex flex-col gap-xs"
    >
      <h2 id="access-alarms-heading" className="font-display text-lg">
        Larm{" "}
        {openCount > 0 ? (
          <span className="rounded-pill bg-danger/10 px-sm py-[2px] text-sm font-semibold text-danger">
            {openCount} att granska
          </span>
        ) : null}
      </h2>
      {error ? <p className="text-sm text-danger">{error}</p> : null}
      {!alarms && !error ? (
        <p className="text-sm text-textSecondary">Hämtar…</p>
      ) : null}
      {alarms && alarms.length === 0 ? (
        <p className="text-sm text-textSecondary">
          Inga avvikelser i fönstret.
        </p>
      ) : null}
      {alarms?.map((a) => {
        const key = a.id;
        return (
          <div
            key={key}
            className={`rounded-md border bg-surface ${
              a.acknowledgedAt
                ? "border-surfaceMuted opacity-70"
                : "border-danger/40"
            }`}
          >
            <div className="flex flex-wrap items-center gap-md p-sm">
              <span className="min-w-[14ch] flex-1">
                <span className="font-semibold">
                  {a.employeeName ?? a.employeeId.slice(0, 8)}
                </span>{" "}
                <span className="text-sm text-textSecondary">
                  {a.employeeRole}
                </span>
                <span className="block text-sm">{alarmText(a)}</span>
              </span>
              <span className="text-sm tabular-nums text-textSecondary">
                {a.day} · {fmtDate(a.firstAccessAt)}–
                {fmtDate(a.lastAccessAt).split(" ").pop()}
              </span>
              <button
                type="button"
                onClick={() => setOpen(open === key ? null : key)}
                aria-expanded={open === key}
                className="rounded-pill border border-surfaceMuted px-md py-xs text-sm"
              >
                {open === key ? "Dölj raderna" : "Visa raderna"}
              </button>
              {a.acknowledgedAt ? (
                <span className="text-xs text-textSecondary">
                  Granskad {fmtDate(a.acknowledgedAt)}
                </span>
              ) : (
                <button
                  type="button"
                  disabled={busy === key}
                  onClick={() => void acknowledge(key)}
                  className="rounded-pill border border-brand bg-brand-accent px-md py-xs text-sm font-semibold disabled:opacity-50"
                >
                  {busy === key ? "Sparar…" : "Markera som granskad"}
                </button>
              )}
            </div>
            {open === key ? (
              <div className="border-t border-surfaceMuted">
                <Entries
                  employeeId={a.employeeId}
                  days={days}
                  onlyWithoutRelationship={a.kind === "no_relationship"}
                />
              </div>
            ) : null}
          </div>
        );
      })}
    </section>
  );
}

// TES-1040 — the support access an admin took, one row per grant: who, which
// patient (opaque, as above), the reason she gave, when, until when, and how
// many reads were made under it. The reads themselves are in the log above
// with the basis "support".
function SupportAccessGrants({ days }: { days: number }) {
  const [rows, setRows] = useState<SupportAccessReviewRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setError(null);
    fetchSupportAccessGrants(days)
      .then((r) => live && setRows(r))
      .catch((e) => live && setError(String(e)));
    return () => {
      live = false;
    };
  }, [days]);

  return (
    <section
      aria-labelledby="support-access-heading"
      className="flex flex-col gap-xs"
    >
      <h2 id="support-access-heading" className="font-display text-lg">
        Supportåtkomst
      </h2>
      <p className="max-w-[70ch] text-sm text-textSecondary">
        Patienter som en administratör öppnat i läkar- eller coachvyn (läsläge),
        med orsaken som angavs. Åtkomsten gäller resten av dagen.
      </p>
      {error ? <p className="text-sm text-danger">{error}</p> : null}
      {!rows && !error ? (
        <p className="text-sm text-textSecondary">Hämtar…</p>
      ) : null}
      {rows && rows.length === 0 ? (
        <p className="text-sm text-textSecondary">
          Ingen supportåtkomst i fönstret.
        </p>
      ) : null}
      {rows && rows.length > 0 ? (
        <div className="overflow-x-auto rounded-md border border-surfaceMuted bg-surface">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-textSecondary">
                <th className="p-xs pl-sm">Administratör</th>
                <th className="p-xs">Patient</th>
                <th className="p-xs">Orsak</th>
                <th className="p-xs">Given</th>
                <th className="p-xs">Gäller till</th>
                <th className="p-xs pr-sm text-right">Läsningar</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((g) => (
                <tr
                  key={g.id}
                  className="border-t border-surfaceMuted align-top"
                >
                  <td className="p-xs pl-sm font-semibold">
                    {g.employeeName ?? g.employeeId.slice(0, 8)}
                  </td>
                  {/* Opaque on purpose — the pattern, not the person. */}
                  <td className="p-xs font-mono text-xs">
                    {g.patientId.slice(0, 8)}
                  </td>
                  <td className="p-xs max-w-[40ch] whitespace-pre-wrap break-words">
                    {g.reason ?? (
                      <span className="text-textSecondary">
                        {g.hasReason ? "orsak angiven" : "—"}
                      </span>
                    )}
                  </td>
                  <td className="p-xs tabular-nums text-textSecondary">
                    {fmtDate(g.grantedAt)}
                  </td>
                  <td className="p-xs tabular-nums text-textSecondary">
                    {fmtDate(g.expiresAt)}
                  </td>
                  <td className="p-xs pr-sm text-right tabular-nums">
                    {g.accessCount}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}

export function AccessReviewRoute() {
  const [days, setDays] = useState<number>(ACCESS_REVIEW_DEFAULT_DAYS);
  const [data, setData] = useState<AccessReviewWindow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    fetchAccessReview(days)
      .then(setData)
      .catch((e) => setError(String(e)));
  }, [days]);

  useEffect(load, [load]);

  return (
    <div className="flex flex-col gap-md p-lg">
      <div>
        <h1 className="font-display text-2xl">Åtkomstgranskning</h1>
        <p className="mt-xs max-w-[70ch] text-sm text-textSecondary">
          Vem har öppnat en patientjournal, och fanns det en vårdrelation.
          Patienterna står som id och inte med namn — granskningen ska visa
          mönstret, inte personen.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-sm">
        {WINDOWS.map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => setDays(d)}
            className={`rounded-pill border px-md py-xs text-sm ${
              d === days
                ? "border-brand bg-brand-accent font-semibold"
                : "border-surfaceMuted"
            }`}
          >
            {d} dagar
          </button>
        ))}
      </div>

      <Alarms days={days} />

      <SupportAccessGrants days={days} />

      {error ? <p className="text-sm text-danger">{error}</p> : null}

      {data ? (
        <>
          <p className="text-sm text-textSecondary">
            <span className="font-semibold text-textPrimary">
              {data.totalAccesses}
            </span>{" "}
            åtkomster i fönstret, varav{" "}
            <span
              className={
                data.totalWithoutRelationship > 0
                  ? "font-semibold text-danger"
                  : "font-semibold text-textPrimary"
              }
            >
              {data.totalWithoutRelationship}
            </span>{" "}
            utan aktiv vårdrelation.
          </p>

          {data.rows.length === 0 ? (
            <p className="text-sm text-textSecondary">
              Ingen har öppnat en patientjournal i fönstret.
            </p>
          ) : (
            <div className="flex flex-col gap-xs">
              {data.rows.map((r) => (
                <div
                  key={r.employeeId}
                  className="rounded-md border border-surfaceMuted bg-surface"
                >
                  <button
                    type="button"
                    onClick={() =>
                      setOpen(open === r.employeeId ? null : r.employeeId)
                    }
                    className="flex w-full flex-wrap items-center gap-md p-sm text-left"
                  >
                    <span className="min-w-[14ch] flex-1 font-semibold">
                      {r.employeeName ?? r.employeeId.slice(0, 8)}
                    </span>
                    <span className="text-sm text-textSecondary">
                      {r.employeeRole}
                    </span>
                    <span className="text-sm tabular-nums">
                      {r.accesses} åtkomster · {r.patients} patienter
                    </span>
                    {/* The threshold comes from the contract, so admin and the
                        API agree. It means "look here", not
                        "this is wrong". */}
                    {standsOut(r) ? (
                      <span className="rounded-pill bg-danger/10 px-sm py-[2px] text-xs font-semibold text-danger">
                        {r.withoutRelationship} utan vårdrelation
                      </span>
                    ) : null}
                    <span className="text-xs text-textSecondary">
                      {r.lastAccessAt ? fmtDate(r.lastAccessAt) : "—"}
                    </span>
                  </button>
                  {open === r.employeeId ? (
                    <div className="border-t border-surfaceMuted">
                      <Entries employeeId={r.employeeId} days={days} />
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </>
      ) : !error ? (
        <p className="text-sm text-textSecondary">Hämtar…</p>
      ) : null}
    </div>
  );
}
